<template>
  <div class="monthly-analysis-content">
    <!-- Message d'erreur si les données ne sont pas valides -->
    <div v-if="!isDataValid" class="error-message">
      <h3>
        <svg class="error-icon" viewBox="0 0 24 24" fill="none">
          <path d="M12 9V13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          <path d="M12 17H12.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          <path d="M12 2L2 22H22L12 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        {{ t('DjChatPage.monthlyAnalysis.monthlyAnalysis.error') }}
      </h3>
      <p>{{ t('DjChatPage.monthlyAnalysis.errorMessage') }}</p>
      <details>
        <summary>{{ t('DjChatPage.monthlyAnalysis.debug') }}</summary>
        <pre>{{ JSON.stringify(analysisData, null, 2) }}</pre>
      </details>
    </div>

    <!-- Contenu principal si les données sont valides -->
    <template v-else>
      <!-- Vue d'ensemble globale -->
      <div class="overview-section">
        <h2>
          <svg class="section-icon" viewBox="0 0 24 24" fill="none">
            <path d="M3 13L9 7L13 11L21 3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M21 3H16M21 3V8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          {{ t('DjChatPage.monthlyAnalysis.globalAnalysis') }}
        </h2>
        <div class="overview-cards">
          <div class="overview-card total-ca">
            <div class="card-content">
              <div class="card-label">{{ t('DjChatPage.monthlyAnalysis.totalCA') }}</div>
              <div class="card-value">{{ formatCurrency(getSafeValue('summary.totalCA')) }}</div>
            </div>
          </div>
          <div class="overview-card orders">
            <div class="card-content">
              <div class="card-label">{{ t('DjChatPage.monthlyAnalysis.nbOrders') }}</div>
              <div class="card-value">{{ getSafeValue('summary.nbOrders') }}</div>
            </div>
          </div>
          <div class="overview-card avg-ca">
            <div class="card-content">
              <div class="card-label">{{ t('DjChatPage.monthlyAnalysis.avgCA') }}</div>
              <div class="card-value">{{ formatCurrency(getSafeValue('summary.avgCA')) }}</div>
            </div>
          </div>
          <div class="overview-card units">
            <div class="card-content">
              <div class="card-label">{{ t('DjChatPage.monthlyAnalysis.unitsSold') }}</div>
              <div class="card-value">{{ formatNumber(getSafeValue('summary.units')) }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Graphique d'évolution mensuelle -->
      <div class="charts-section">
        <h3>
          <svg class="section-icon" viewBox="0 0 24 24" fill="none">
            <path d="M3 3V21H21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M7 12L10.5 8.5L13.5 11.5L20 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          {{ t('DjChatPage.monthlyAnalysis.monthlyEvolution') }}
        </h3>
        <div class="charts-container">
          <div class="chart-card">
            <h4>{{ t('DjChatPage.monthlyAnalysis.monthlyCA') }}</h4>
            <div class="chart ca-chart">
              <div class="chart-bars">
                <div 
                  v-for="month in getSafeMonths()" 
                  :key="month.month"
                  class="chart-bar"
                  :style="{ height: getBarHeight(month.totalCA, getMaxCA()) + '%' }"
                >
                  <div class="bar-value">{{ formatCurrency(month.totalCA, true) }}</div>
                </div>
              </div>
              <div class="chart-labels">
                <div v-for="month in getSafeMonths()" :key="month.month" class="chart-label">
                  {{ getMonthName(month.month) }}
                </div>
              </div>
            </div>
          </div>

          <div class="chart-card">
            <h4>{{ t('DjChatPage.monthlyAnalysis.monthlyOrders') }}</h4>
            <div class="chart orders-chart">
              <div class="chart-bars">
                <div 
                  v-for="month in getSafeMonths()" 
                  :key="month.month"
                  class="chart-bar orders-bar"
                  :style="{ height: getBarHeight(month.nbOrders, getMaxOrders()) + '%' }"
                >
                  <div class="bar-value">{{ month.nbOrders }}</div>
                </div>
              </div>
              <div class="chart-labels">
                <div v-for="month in getSafeMonths()" :key="month.month" class="chart-label">
                  {{ getMonthName(month.month) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Analyse par Catégories -->
      <div class="categories-section">
        <h3>
          <svg class="section-icon" viewBox="0 0 24 24" fill="none">
            <path d="M22 19C22 19.6 21.6 20 21 20H3C2.4 20 2 19.6 2 19V5C2 4.4 2.4 4 3 4H7L9 6H21C21.6 6 22 6.4 22 7V19Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          {{ t('DjChatPage.monthlyAnalysis.categoryDistribution') }}
        </h3>
        <div class="categories-container">
          <!-- Graphique en secteurs des catégories -->
          <div class="categories-chart-card">
            <h4>{{ t('DjChatPage.monthlyAnalysis.categoryCA') }}</h4>
            <div class="categories-chart">
              <div class="pie-chart-categories">
                <svg viewBox="0 0 200 200">
                  <path
                    v-for="(category, index) in getSafeTopCategories()" 
                    :key="category.categorie"
                    :d="getCategoryPath(category.share, index)"
                    :fill="getCategoryColor(index)"
                    class="pie-segment-category"
                  />
                </svg>
              </div>
              <div class="categories-legend">
                <div 
                  v-for="(category, index) in getSafeTopCategories()" 
                  :key="category.categorie"
                  class="legend-item-category"
                >
                  <div class="legend-color-category" :style="{ backgroundColor: getCategoryColor(index) }"></div>
                  <div class="legend-text-category">
                    <span class="category-name">{{ category.categorie }}</span>
                    <span class="category-share">{{ category.share }}%</span>
                    <span class="category-ca">{{ formatCurrency(category.ca) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Top catégories en barres -->
          <div class="categories-bars-card">
            <h4>{{ t('DjChatPage.monthlyAnalysis.topCategories') }}</h4>
            <div class="categories-bars">
              <div 
                v-for="(category, index) in getSafeTopCategories()" 
                :key="category.categorie"
                class="category-bar-item"
              >
                <div class="category-info">
                  <span class="category-name">{{ category.categorie }}</span>
                  <span class="category-value">{{ formatNumber(category.units) }} unités</span>
                </div>
                <div class="category-bar">
                  <div 
                    class="category-bar-fill"
                    :style="{ 
                      width: getCategoryBarWidth(category.units) + '%',
                      backgroundColor: getCategoryColor(index)
                    }"
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Distribution complète des catégories -->
        <div class="categories-distribution">
          <h4>
            <svg class="section-icon" viewBox="0 0 24 24" fill="none">
              <path d="M3 3V21H21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              <rect x="4" y="7" width="3" height="10" fill="currentColor" />
              <rect x="8.5" y="4" width="3" height="13" fill="currentColor" />
              <rect x="13" y="9" width="3" height="8" fill="currentColor" />
              <rect x="17.5" y="6" width="3" height="11" fill="currentColor" />
            </svg>
            {{ t('DjChatPage.monthlyAnalysis.fullCategoryDistribution') }}
          </h4>
          <div class="distribution-grid">
            <div 
              v-for="(category, index) in getSafeCategoriesDistribution()" 
              :key="category.categorie"
              class="distribution-card"
            >
              <div class="distribution-header">
                <div class="category-color-indicator" :style="{ backgroundColor: getCategoryColor(index) }"></div>
                <h5>{{ category.categorie }}</h5>
              </div>
              <div class="distribution-stats">
                <div class="stat">
                  <span class="label">{{ t('DjChatPage.monthlyAnalysis.ca') }}:</span>
                  <span class="value">{{ formatCurrency(category.ca) }}</span>
                </div>
                <div class="stat">
                  <span class="label">{{ t('DjChatPage.monthlyAnalysis.share') }}:</span>
                  <span class="value">{{ category.share }}%</span>
                </div>
                <div class="stat">
                  <span class="label">{{ t('DjChatPage.monthlyAnalysis.units') }}:</span>
                  <span class="value">{{ formatNumber(category.units) }}</span>
                </div>
                <div class="stat">
                  <span class="label">{{ t('DjChatPage.monthlyAnalysis.avgPrice') }}:</span>
                  <span class="value">{{ formatCurrency(category.ca / category.units) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Top Produits Global -->
      <div class="top-products-section">
        <h3>
          <svg class="section-icon" viewBox="0 0 24 24" fill="none">
            <path d="M6 9L12 2L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M12 2V15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M5 22H19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          {{ t('DjChatPage.monthlyAnalysis.topProducts') }}
        </h3>
        <div class="top-products-grid">
          <div 
            v-for="(product, index) in getSafeTopProducts()" 
            :key="product.id"
            class="top-product-card"
            :class="getRankClass(index)"
          >
            <div class="product-rank">{{ index + 1 }}</div>
            <div class="product-info">
              <div class="product-name">{{ product.name }}</div>
              <div class="product-stats">
                <div class="stat">
                  <span class="label">{{ t('DjChatPage.monthlyAnalysis.ca') }}:</span>
                  <span class="value">{{ formatCurrency(product.ca) }}</span>
                </div>
                <div class="stat">
                  <span class="label">{{ t('DjChatPage.monthlyAnalysis.quantity') }}:</span>
                  <span class="value">{{ formatNumber(product.qty) }}</span>
                </div>
                <div class="stat">
                  <span class="label">{{ t('DjChatPage.monthlyAnalysis.unitPrice') }}:</span>
                  <span class="value">{{ formatCurrency(product.ca / product.qty) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Détail par mois -->
      <div class="months-detail-section">
        <h3>
          <svg class="section-icon" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="currentColor" stroke-width="2" />
            <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" stroke-width="2" />
            <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" stroke-width="2" />
            <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" stroke-width="2" />
          </svg>
          {{ t('DjChatPage.monthlyAnalysis.monthDetail') }}
        </h3>
        <div class="months-grid">
          <div 
            v-for="month in getSafeMonths()" 
            :key="month.month"
            class="month-card"
          >
            <div class="month-header">
              <h4>{{ getMonthName(month.month) }}</h4>
              <div class="month-stats">
                <div class="stat">
                  <span class="label">{{ t('DjChatPage.monthlyAnalysis.ca') }}:</span>
                  <span class="value">{{ formatCurrency(month.totalCA) }}</span>
                </div>
                <div class="stat">
                  <span class="label">{{ t('DjChatPage.monthlyAnalysis.orders') }}:</span>
                  <span class="value">{{ month.nbOrders }}</span>
                </div>
                <div class="stat">
                  <span class="label">{{ t('DjChatPage.monthlyAnalysis.units') }}:</span>
                  <span class="value">{{ formatNumber(month.units) }}</span>
                </div>
              </div>
            </div>
          
            <div class="month-top-products">
              <h5>{{ t('DjChatPage.monthlyAnalysis.topProducts') }}</h5>
              <div class="products-list">
                <div 
                  v-for="(product, index) in month.topProducts.slice(0, 3)" 
                  :key="product.id"
                  class="product-item"
                >
                  <div class="product-rank">{{ index + 1 }}</div>
                  <div class="product-details">
                    <div class="product-name">{{ product.name }}</div>
                    <div class="product-metrics">
                      <span class="qty">{{ formatNumber(product.qty) }} unités</span>
                      <span class="ca">{{ formatCurrency(product.ca) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Graphique de répartition des ventes -->
      <div class="distribution-section">
        <h3>
          <svg class="section-icon" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" />
            <path d="M12 2L12 12L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          {{ t('DjChatPage.monthlyAnalysis.salesDistribution') }}
        </h3>
        <div class="distribution-chart">
          <div class="pie-chart">
            <svg viewBox="0 0 200 200">
              <path
                v-for="(month, index) in getSafeMonths()" 
                :key="month.month"
                :d="getMonthPath(month.totalCA, index)"
                :fill="getMonthColor(index)"
                class="pie-segment"
              />
            </svg>
          </div>
          <div class="pie-legend">
            <div 
              v-for="(month, index) in getSafeMonths()" 
              :key="month.month"
              class="legend-item"
            >
              <div class="legend-color" :style="{ backgroundColor: getMonthColor(index) }"></div>
              <div class="legend-text">
                <span class="month-name">{{ getMonthName(month.month) }}</span>
                <span class="percentage">{{ getPercentage(month.totalCA) }}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { computed } from 'vue';

export default {
  name: 'MonthlyAnalysisContent',
  props: {
    analysisData: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    const { t } = useI18n()

    // Computed properties
    const actualAnalysisData = computed(() => {
      // Si l'objet contient directement summary et months
      if (props.analysisData && props.analysisData.summary && props.analysisData.months) {
        return props.analysisData;
      }
      
      // Si l'objet contient action et analysisData (format N8N)
      if (props.analysisData && props.analysisData.analysisData) {
        return props.analysisData.analysisData;
      }
      
      return null;
    });
    
    const isDataValid = computed(() => {
      const data = actualAnalysisData.value;
      return data && 
             data.summary && 
             data.months && 
             Array.isArray(data.months) &&
             typeof data.summary.totalCA !== 'undefined';
    });

    // Methods
    const getSafeValue = (path) => {
      try {
        const keys = path.split('.');
        let value = actualAnalysisData.value;
        
        for (const key of keys) {
          if (value && typeof value === 'object' && key in value) {
            value = value[key];
          } else {
            return 0; // Valeur par défaut
          }
        }
        
        return value !== undefined && value !== null ? value : 0;
      } catch (error) {
        console.warn('Erreur lors de l\'accès à la propriété:', path, error);
        return 0;
      }
    };

    const getSafeMonths = () => {
      const data = actualAnalysisData.value;
      return data && data.months && Array.isArray(data.months) 
        ? data.months 
        : [];
    };

    const getSafeTopProducts = () => {
      const data = actualAnalysisData.value;
      return data && 
             data.summary && 
             data.summary.topProducts && 
             Array.isArray(data.summary.topProducts)
        ? data.summary.topProducts 
        : [];
    };

    const getSafeTopCategories = () => {
      const data = actualAnalysisData.value;
      return data && 
             data.summary && 
             data.summary.topCategories && 
             Array.isArray(data.summary.topCategories)
        ? data.summary.topCategories 
        : [];
    };

    const getSafeCategoriesDistribution = () => {
      const data = actualAnalysisData.value;
      return data && 
             data.summary && 
             data.summary.categoriesDistribution && 
             Array.isArray(data.summary.categoriesDistribution)
        ? data.summary.categoriesDistribution 
        : [];
    };

    const formatCurrency = (amount, short = false) => {
      if (!amount && amount !== 0) return '0,00 €';
      
      if (short && amount >= 1000) {
        return (amount / 1000).toFixed(1) + 'k€';
      }
      
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount);
    };

    const formatNumber = (number) => {
      if (!number && number !== 0) return '0';
      return new Intl.NumberFormat('fr-FR').format(number);
    };

    const getMonthName = (monthStr) => {
      const months = {
        '01': 'Janvier', '02': 'Février', '03': 'Mars', '04': 'Avril',
        '05': 'Mai', '06': 'Juin', '07': 'Juillet', '08': 'Août',
        '09': 'Septembre', '10': 'Octobre', '11': 'Novembre', '12': 'Décembre'
      };
      
      const monthNum = monthStr.split('-')[1];
      return months[monthNum] || monthStr;
    };

    const getMaxCA = () => {
      const months = getSafeMonths();
      return months.length > 0 ? Math.max(...months.map(m => m.totalCA || 0)) : 1;
    };

    const getMaxOrders = () => {
      const months = getSafeMonths();
      return months.length > 0 ? Math.max(...months.map(m => m.nbOrders || 0)) : 1;
    };

    const getBarHeight = (value, max) => {
      if (!max || max === 0) return 0;
      return Math.max((value / max) * 80, 5); // Min 5% pour la visibilité
    };

    const getRankClass = (index) => {
      const classes = ['first', 'second', 'third', 'fourth', 'fifth'];
      return classes[index] || '';
    };

    const getMonthColor = (index) => {
      const colors = [
        '#ff6b6b', // Rouge
        '#4ecdc4', // Turquoise
        '#45b7d1', // Bleu
        '#f9ca24', // Jaune
        '#f0932b', // Orange
        '#eb4d4b', // Rouge foncé
        '#6c5ce7'  // Violet
      ];
      return colors[index % colors.length];
    };

    const getMonthPath = (value, index) => {
      const months = getSafeMonths();
      const centerX = 100;
      const centerY = 100;
      const radius = 90;
      
      // Calculer l'angle de début en degrés
      let startAngle = 0;
      for (let i = 0; i < index && i < months.length; i++) {
        const percentage = getPercentage(months[i].totalCA || 0);
        startAngle += (percentage / 100) * 360;
      }
      
      // Angle de fin
      const percentage = getPercentage(value);
      const endAngle = startAngle + (percentage / 100) * 360;
      
      // Convertir en radians
      const startAngleRad = (startAngle * Math.PI) / 180;
      const endAngleRad = (endAngle * Math.PI) / 180;
      
      // Calculer les coordonnées
      const startX = centerX + radius * Math.cos(startAngleRad);
      const startY = centerY + radius * Math.sin(startAngleRad);
      const endX = centerX + radius * Math.cos(endAngleRad);
      const endY = centerY + radius * Math.sin(endAngleRad);
      
      // Détermine si l'arc est grand (> 180°)
      const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;
      
      // Construire le chemin SVG
      return [
        `M ${centerX} ${centerY}`, // Aller au centre
        `L ${startX} ${startY}`, // Ligne vers le début de l'arc
        `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY}`, // Arc
        'Z' // Fermer le chemin vers le centre
      ].join(' ');
    };

    const getPercentage = (value) => {
      const total = getSafeValue('summary.totalCA');
      if (!total || total === 0) return 0;
      return Math.round((value / total) * 100 * 10) / 10;
    };

    const getCategoryColor = (index) => {
      const colors = [
        '#ff6b6b', // Rouge - SPIRITUEUX
        '#4ecdc4', // Turquoise - SOFT DRINKS
        '#45b7d1', // Bleu - VINS FRANCAIS
        '#f9ca24', // Jaune - VINS ETRANGERS
        '#f0932b', // Orange - JUS DE FRUITS
        '#eb4d4b', // Rouge foncé - CAFE
        '#6c5ce7', // Violet - SIROPS & CONCENTRÉS
        '#a55eea', // Mauve - BIERES
        '#26de81', // Vert - MATÉRIELS
        '#fd79a8'  // Rose - CIDERS
      ];
      return colors[index % colors.length];
    };

    const getCategoryPath = (share, index) => {
      const categories = getSafeTopCategories();
      const centerX = 100;
      const centerY = 100;
      const radius = 90;
      
      // Calculer l'angle de début en degrés
      let startAngle = 0;
      for (let i = 0; i < index && i < categories.length; i++) {
        startAngle += (categories[i].share / 100) * 360;
      }
      
      // Angle de fin
      const endAngle = startAngle + (share / 100) * 360;
      
      // Convertir en radians
      const startAngleRad = (startAngle * Math.PI) / 180;
      const endAngleRad = (endAngle * Math.PI) / 180;
      
      // Calculer les coordonnées
      const startX = centerX + radius * Math.cos(startAngleRad);
      const startY = centerY + radius * Math.sin(startAngleRad);
      const endX = centerX + radius * Math.cos(endAngleRad);
      const endY = centerY + radius * Math.sin(endAngleRad);
      
      // Détermine si l'arc est grand (> 180°)
      const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;
      
      // Construire le chemin SVG
      return [
        `M ${centerX} ${centerY}`, // Aller au centre
        `L ${startX} ${startY}`, // Ligne vers le début de l'arc
        `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY}`, // Arc
        'Z' // Fermer le chemin vers le centre
      ].join(' ');
    };

    const getCategoryBarWidth = (units) => {
      const categories = getSafeTopCategories();
      if (categories.length === 0) return 0;
      
      const maxUnits = Math.max(...categories.map(c => c.units || 0));
      if (!maxUnits || maxUnits === 0) return 0;
      
      return Math.max((units / maxUnits) * 100, 5); // Min 5% pour la visibilité
    };

    // Return all reactive data, computed properties and methods for template
    return {
      // Computed
      actualAnalysisData,
      isDataValid,
      
      // Methods
      getSafeValue,
      getSafeMonths,
      getSafeTopProducts,
      getSafeTopCategories,
      getSafeCategoriesDistribution,
      formatCurrency,
      formatNumber,
      getMonthName,
      getMaxCA,
      getMaxOrders,
      getBarHeight,
      getRankClass,
      getMonthColor,
      getMonthPath,
      getPercentage,
      getCategoryColor,
      getCategoryPath,
      getCategoryBarWidth,
      t
    };
  },
};
</script>

<style lang="scss" scoped>
@use './MonthlyAnalysisContent.scss';
</style> 